import queryClient from "single-spa/react-app/query-client";

import type {
    IUserKeywordGroup,
    IInitialKeywordGroup,
    IShareKeywordGroupPayload,
    IDuplicateKeywordGroupPayload,
} from "../keywordGroups.types";
import {
    keywordGroupsOptions,
    keywordGroupsKeys,
    createKeywordGroup,
    updateKeywordGroup,
    deleteKeywordGroup,
    shareKeywordGroup,
    removeKeywordGroupSharing,
    duplicateKeywordGroup,
} from "../queries/keywordGroupsQueries";

/**
 * @deprecated Use keywordGroupsApiClientQueryClient instead.
 * This query client will be removed in a future version.
 * New implementations should use the API client version for better type safety and consistency.
 */
export const keywordGroupsQueryClient = {
    getUserGroups: async (): Promise<IUserKeywordGroup[]> => {
        return await queryClient.fetchQuery<IUserKeywordGroup[]>(keywordGroupsOptions.user());
    },

    getSharedGroups: async (): Promise<IUserKeywordGroup[]> => {
        return await queryClient.fetchQuery<IUserKeywordGroup[]>(keywordGroupsOptions.shared());
    },

    getGroupById: async (id: string): Promise<IUserKeywordGroup | null> => {
        return await queryClient.fetchQuery<IUserKeywordGroup>(keywordGroupsOptions.byId(id));
    },

    getAllGroups: async (): Promise<{ userGroups: IUserKeywordGroup[]; sharedGroups: IUserKeywordGroup[] }> => {
        const [userGroups, sharedGroups] = await Promise.all([
            queryClient.fetchQuery<IUserKeywordGroup[]>(keywordGroupsOptions.user()),
            queryClient.fetchQuery<IUserKeywordGroup[]>(keywordGroupsOptions.shared()),
        ]);

        return { userGroups, sharedGroups };
    },

    createGroup: async (group: IInitialKeywordGroup): Promise<IUserKeywordGroup | undefined> => {
        await createKeywordGroup(group);
        await queryClient.invalidateQueries({ queryKey: keywordGroupsKeys.user() });
        const newGroupList = await keywordGroupsQueryClient.getUserGroups();
        const newGroup = newGroupList.find((g) => g.Name === group.Name);
        return newGroup;
    },

    updateGroup: async (group: IUserKeywordGroup): Promise<IUserKeywordGroup | null> => {
        await updateKeywordGroup(group);
        await queryClient.invalidateQueries({ queryKey: keywordGroupsKeys.user() });
        await queryClient.invalidateQueries({ queryKey: keywordGroupsKeys.byId(group.Id) });
        const updatedGroup = await keywordGroupsQueryClient.getGroupById(group.Id);
        return updatedGroup;
    },

    deleteGroup: async (group: IUserKeywordGroup): Promise<void> => {
        await deleteKeywordGroup(group.Id, group.Name, group.Keywords);

        queryClient.setQueryData(keywordGroupsKeys.user(), (old: IUserKeywordGroup[] = []) =>
            old.filter((g) => g.Id !== group.Id),
        );

        await queryClient.invalidateQueries({ queryKey: keywordGroupsKeys.user() });
    },

    shareGroup: async (groupId: string, payload: IShareKeywordGroupPayload): Promise<void> => {
        await shareKeywordGroup(groupId, payload);
        await queryClient.invalidateQueries({ queryKey: keywordGroupsKeys.user() });
        await queryClient.invalidateQueries({ queryKey: keywordGroupsKeys.shared() });
    },

    removeGroupSharing: async (groupId: string): Promise<void> => {
        await removeKeywordGroupSharing(groupId);
        await queryClient.invalidateQueries({ queryKey: keywordGroupsKeys.user() });
        await queryClient.invalidateQueries({ queryKey: keywordGroupsKeys.shared() });
    },

    duplicateGroup: async (payload: IDuplicateKeywordGroupPayload): Promise<IUserKeywordGroup> => {
        const duplicatedGroup = await duplicateKeywordGroup(payload);

        queryClient.setQueryData(keywordGroupsKeys.user(), (old: IUserKeywordGroup[] = []) => [
            ...old,
            duplicatedGroup,
        ]);

        await queryClient.invalidateQueries({ queryKey: keywordGroupsKeys.user() });

        return duplicatedGroup;
    },

    prefetchUserGroups: async (): Promise<void> => {
        await queryClient.prefetchQuery(keywordGroupsOptions.user());
    },

    prefetchSharedGroups: async (): Promise<void> => {
        await queryClient.prefetchQuery(keywordGroupsOptions.shared());
    },

    prefetchGroupById: async (id: string): Promise<void> => {
        await queryClient.prefetchQuery(keywordGroupsOptions.byId(id));
    },

    invalidateAll: async (): Promise<void> => {
        await queryClient.invalidateQueries({ queryKey: keywordGroupsKeys.all });
    },

    invalidateUserGroups: async (): Promise<void> => {
        await queryClient.invalidateQueries({ queryKey: keywordGroupsKeys.user() });
    },

    invalidateSharedGroups: async (): Promise<void> => {
        await queryClient.invalidateQueries({ queryKey: keywordGroupsKeys.shared() });
    },
};
